import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ayeApi } from '../../core';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';

const TIMEOUT = 60;

interface OtpError {
  message: string;
  code?: string;
}

interface EmailOtpVerificationDialogProps {
  open: boolean;
  pendingEmail: string;
  isLoading: boolean;
  error: OtpError | null;
  onVerify: (otp: string) => void;
  onClose: () => void;
}

const EmailOtpVerificationDialog = ({
  open,
  pendingEmail,
  isLoading,
  error,
  onVerify,
  onClose
}: EmailOtpVerificationDialogProps) => {
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(TIMEOUT);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [suppressParentError, setSuppressParentError] = useState(false);

  const { mutate: sendOtpMutate, isPending: isOtpPending, error: sendOtpError } = useMutation({
    mutationKey: ['sendEmailOtp', pendingEmail],
    mutationFn: async () => {
      const res = await ayeApi.validateEmail({ body: { email: pendingEmail } });
      if (!res.success) throw new Error(res.error.message);
    },
    onSuccess: () => {
      setIsCountdownActive(true);
      setCountdown(TIMEOUT);
      setOtp('');
      setSuppressParentError(true);
    },
    onError: () => {
      setIsCountdownActive(false);
      setCountdown(0);
      setOtp('');
    }
  });

  // Send OTP when dialog opens
  useEffect(() => {
    if (open && pendingEmail) {
      sendOtpMutate();
    }
  }, [open, pendingEmail, sendOtpMutate]);

  // Tick countdown
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCountdownActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setIsCountdownActive(false);
    }
    return () => clearTimeout(timer);
  }, [isCountdownActive, countdown]);

  // Detect OTP_FAILED_ATTEMPTS from parent verify error
  useEffect(() => {
    if (error?.code === 'OTP_FAILED_ATTEMPTS') {
      setCountdown(0);
      setIsCountdownActive(false);
    }
    // New verify error arrived — stop suppressing parent error
    if (error) {
      setSuppressParentError(false);
    }
  }, [error]);

  const handleResend = () => {
    if (isOtpPending || countdown !== 0) return;
    sendOtpMutate();
  };

  const handleVerify = () => {
    onVerify(otp);
    setOtp('');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isLoading) {
      setOtp('');
      onClose();
    }
  };

  const displayError =
    sendOtpError?.message ?? (suppressParentError ? '' : error?.message) ?? '';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[24rem] rounded-3xl"
        aria-describedby="email-otp-verification-modal"
      >
        <DialogHeader className="mt-10">
          <DialogTitle className="text-center">Email Verification</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center">
          <p className="text-md my-2 text-center">One Time Password</p>
          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <p
          className={cn(
            'mt-4 text-center text-xs',
            displayError ? 'text-[#FF5E18]' : 'text-[#aac4b9]'
          )}
        >
          {displayError ||
            `An OTP has been sent to ${pendingEmail}. Please enter it above to confirm the email change.`}
        </p>

        {isCountdownActive && countdown > 0 && (
          <p className="mt-2 text-center text-sm">
            Resend OTP in <strong>{countdown}s</strong>
          </p>
        )}

        <Button
          className="mt-3 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
          onClick={handleVerify}
          disabled={isLoading || isOtpPending || otp.length !== 6}
          type="button"
        >
          {isLoading ? 'Verifying...' : 'Verify & Save'}
        </Button>

        {!isCountdownActive && (
          <Button
            variant="outline"
            className="text-md mt-2 rounded-full border-2 border-[#5CB891] bg-white font-semibold text-[#5CB891] hover:bg-white hover:text-[#5CB891]"
            onClick={handleResend}
            disabled={isOtpPending}
            type="button"
          >
            {isOtpPending ? 'Sending...' : 'Resend OTP'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailOtpVerificationDialog;
